"use client";

import { safeCapture } from "@/lib/analytics/safeCapture";
import { useState } from "react";
import { Button, type ButtonProps } from "@/components/ui/Button";
import type { CampfireChatSource } from "@/lib/api/recommendations/types";
import { dynamicWithRetry } from "@/lib/chunks/dynamicWithRetry";

const CampfireChatModal = dynamicWithRetry(
  () => import("./CampfireChatModal").then((m) => m.CampfireChatModal),
  { surface: "campfire" },
);

type CampfireChatButtonProps = {
  source: CampfireChatSource;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  className?: string;
};

export function CampfireChatButton({
  source,
  variant = "primary",
  size,
  className,
}: CampfireChatButtonProps) {
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  // Incremented on every open and passed as the modal's `key` — forces a full
  // remount instead of reusing the previous instance's state. Without this,
  // isSuccess/requestId/form values from a prior open survive: the Done button
  // closes via the parent's onOpenChange directly (bypassing the <Modal>
  // wrapper's own reset-on-close), so reopening can show a stuck success panel,
  // and an Escape-then-reopen-then-edit-then-resubmit reuses the stale
  // requestId with a changed payload, hitting an idempotency_conflict 400.
  // Mirrors RecommendationViewer.tsx's enquireKey for EnquireModal.
  const [campfireKey, setCampfireKey] = useState(0);

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        className={className}
        onClick={() => {
          safeCapture("campfire_chat_opened", { source });
          setCampfireKey((k) => k + 1);
          setOpen(true);
          setHasOpened(true);
        }}
      >
        Campfire chat
      </Button>
      {hasOpened && (
        <CampfireChatModal
          key={campfireKey}
          open={open}
          onOpenChange={setOpen}
          source={source}
        />
      )}
    </>
  );
}
